import React, { useState, useRef, useEffect, useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import vpgLogoCondensed from '../../assets/images/flexcoa-logo-condensed.png';
import vpgLogoHome from '../../assets/images/flexcoa-logo-condensed-green.png';
import { Menu as MenuIcon, X, ChevronRight, LogOut, LayoutDashboard, UserCircle, Wrench, HelpCircle, BookOpen, Building2, FileText } from 'lucide-react';
import NotificationBell from '../Notifications/NotificationBell';
import { motion, AnimatePresence } from 'framer-motion';
import { AuthContext } from '../../contexts/AuthContext';
import studyUtil from '../../utils/StudyUtil';
import { isLocalDevLayoutActive } from '../../utils/localDevLayout';
import { isParticipantDomain } from '../../utils/participantDomain';
import { useTheme } from '../../contexts/ThemeContext';
import './hideParticipantButton.css'; // Import the CSS file to hide the button


const HomeNavMenu = ({ isAuthenticated, setIsAuthenticated, isMenuOpen, setIsMenuOpen, isDev }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, user, userType, userPermissions } = useContext(AuthContext);
  const { resolvedTheme } = useTheme();
  const isDarkMode = resolvedTheme === 'dark';
  const paths = location.pathname.split('/').filter(x => x);
  const [hasOnlyOneStudy, setHasOnlyOneStudy] = useState(null); // null = checking, false = multiple studies, true = one study
  const menuRef = useRef(null);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isLocalDevMode, setIsLocalDevMode] = useState(() => isLocalDevLayoutActive());

  // Match LocalDevBanner + screen builder without waiting for body.local-dev-mode (see localDevLayout.js)
  useEffect(() => {
    const sync = () => setIsLocalDevMode(isLocalDevLayoutActive());

    sync();

    const observer = new MutationObserver(sync);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  const noMenuPaths = ['', 'login', 'forgot-password', 'register', 'assessment'];
  const isSponsorSelector = paths[0] === 'sponsor-selector';
  const isParticipantDashboard = paths[0] === 'participant-dashboard';
  const isSurveyTaking = paths[0] === 'survey-taking';
  const isFAQ = paths[0] === 'faq';
  const isParticipantFlow = userType === 'participant' || isParticipantDashboard || isSurveyTaking || isFAQ;

  // Participant routes must always render in light mode — strip the `dark` class
  // from <html> while a participant flow is active and prevent ThemeProvider from
  // re-adding it via a MutationObserver.
  useEffect(() => {
    if (!isParticipantFlow) return;

    const ensureLight = () => document.documentElement.classList.remove('dark');
    ensureLight();

    const observer = new MutationObserver(ensureLight);
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });

    return () => {
      observer.disconnect();
      if (resolvedTheme === 'dark') {
        document.documentElement.classList.add('dark');
      }
    };
  }, [isParticipantFlow, resolvedTheme]);
  const isParticipantRelatedPage = isParticipantFlow || location.pathname.includes('participant');
  
  // Check for participant page by looking at document classes or URL patterns
  const isParticipantPage = location.pathname.includes('participant') || 
                          document.body.classList.contains('participant-flow') || 
                          paths.some(path => path.includes('participant'));
  const shouldShowMenu = !noMenuPaths.includes(paths[0]);

  // Check if user has only one study
  useEffect(() => {
    const checkStudyCount = async () => {
      if (user && !isParticipantFlow) {
        // Skip study count check if user is internal and has no valid sponsor selected
        if (user.is_internal) {
          try {
            const selectedSponsorStr = localStorage.getItem('selectedSponsor');
            if (!selectedSponsorStr) {
              console.debug('[HOME_NAV_MENU] Skipping study count check - no sponsor selected');
              setHasOnlyOneStudy(null);
              return;
            }
            const selectedSponsor = JSON.parse(selectedSponsorStr);
            if (!selectedSponsor || !selectedSponsor.id) {
              console.debug('[HOME_NAV_MENU] Skipping study count check - invalid sponsor data');
              setHasOnlyOneStudy(null);
              return;
            }
          } catch (e) {
            console.debug('[HOME_NAV_MENU] Skipping study count check - error parsing sponsor:', e);
            setHasOnlyOneStudy(null);
            return;
          }
        }
        
        try {
          console.debug('[HOME_NAV_MENU] Starting study count check for user:', user.email);
          // Clear cache to force fresh check
          studyUtil.clearCache();
          const onlyOneStudy = await studyUtil.hasOnlyOneStudy();
          console.debug('[HOME_NAV_MENU] Study count check result:', onlyOneStudy);
          setHasOnlyOneStudy(onlyOneStudy);
        } catch (error) {
          console.error('[HOME_NAV_MENU] Error checking study count:', error);
        }
      } else {
        console.debug('[HOME_NAV_MENU] No user or participant flow, skipping study count check');
      }
    };

    checkStudyCount();
  }, [user, isParticipantFlow]);

  // Handle scroll effect for sticky header
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  
  // Set data-page attribute on body when on participant pages
  useEffect(() => {
    const isParticipantPage = location.pathname.includes('participant');
    
    if (isParticipantPage) {
      document.body.setAttribute('data-page', 'participant');
    } else {
      document.body.removeAttribute('data-page');
    }
    
    return () => {
      document.body.removeAttribute('data-page');
    };
  }, [location.pathname]);

  // Check if current path matches menu item
  const isActive = (path) => {
    if (!path) return false;
    return location.pathname.startsWith(`/${path}`);
  };

  const handleLogoClick = () => {
    if (isParticipantFlow && userType === 'participant') {
      navigate('/participant-dashboard');
    } else {
      navigate('/studies');
    }
  };

  let menuItems = isParticipantFlow ? [
    {
      icon: HelpCircle,
      label: 'FAQ',
      path: 'faq',
      onClick: () => navigate('/faq')
    },
    {
      icon: LogOut,
      label: 'Logout',
      path: 'logout',
      onClick: async () => {
        try {
          // Let the AuthContext handle redirection based on user type
          await logout();
          // No navigation here - AuthContext will redirect to /#studycode
        } catch (error) {
          console.error('Logout failed:', error);
          // Force logout even if API call fails
          setIsAuthenticated(false);
          // No navigation - let AuthContext handle it
        }
      }
    }
  ] : [
    // Only show Studies option if user has more than one study
    ...(hasOnlyOneStudy === false ? [{
      icon: LayoutDashboard,
      label: 'Studies',
      path: 'studies',
      onClick: () => navigate('/studies')
    }] : []),
    // Add sponsor selector.
    // For internal users, keep the original behavior of only showing when there are multiple sponsors.
    // For superusers, always show this option regardless of sponsor count so they can jump into the selector.
    ...(((user?.is_internal && userPermissions?.can_select_sponsor && userPermissions?.sponsor_count > 1) || user?.is_superuser) ? [{
      icon: Building2,
      label: 'Select Sponsor', 
      path: 'sponsor-selector',
      onClick: () => navigate('/sponsor-selector', { state: { from: 'mainMenu' } })
    }] : []),
    // Add Study Builder for users with can_access_study_builder permission
    ...(user?.is_internal && userPermissions?.can_access_study_builder ? [{
      icon: BookOpen,
      label: 'Study Builder',
      path: 'studybuilder',
      onClick: () => navigate('/studybuilder')
    }] : []),
    // Add Assessment Builder for users with can_access_assessment_builder permission
    ...(user?.is_internal && userPermissions?.can_access_assessment_builder ? [{
      icon: FileText,
      label: 'Assessment Builder',
      path: 'assessment-builder',
      onClick: () => navigate('/assessment-builder')
    }] : []),
    {
      icon: UserCircle,
      label: 'Profile',
      path: 'profile',
      onClick: () => navigate('/profile')
    },
    {
      icon: LogOut,
      label: 'Logout',
      path: 'logout',
      onClick: async () => {
        try {
          // Let the AuthContext handle redirection based on user type
          await logout();
          // No manual navigation - AuthContext will handle it
        } catch (error) {
          console.error('Logout failed:', error);
          // Force logout even if API call fails
          setIsAuthenticated(false);
          // No manual navigation - let AuthContext handle it
        }
      }
    }
  ];

  // Add development menu item if in dev mode
  /*console.log('Development Link Debug:', {
    isDev,
    isParticipantFlow,
    userType,
    user_is_superuser: user?.is_superuser,
    user_email: user?.email
  });
  */
  if (isDev && !isParticipantFlow) {
    menuItems.push({
      icon: Wrench,
      label: 'Development',
      path: 'development',
      onClick: () => navigate('/development')
    });
  }

  // On the sponsor selector screen, only show Profile and Logout
  if (isSponsorSelector) {
    menuItems = menuItems.filter(item => item.label === 'Profile' || item.label === 'Logout');
  }

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check for participant login page with hash pattern
  const isParticipantLoginWithHash = window.location.hash && window.location.hash.length > 1;
  
  // Set participant page flags when on login hash pages
  useEffect(() => {
    const checkHash = () => {
      // Specifically check for hash-based participant login
      const isParticipantLoginPage = window.location.hash && window.location.hash.length > 1;
      if (isParticipantLoginPage) {
        document.body.setAttribute('data-page', 'participant-login');
      } else if (location.pathname.includes('participant')) {
        document.body.setAttribute('data-page', 'participant');
      } else {
        document.body.removeAttribute('data-page');
      }
    };
    
    // Initial check
    checkHash();
    
    // Set up hash change listener
    window.addEventListener('hashchange', checkHash);
    
    return () => {
      window.removeEventListener('hashchange', checkHash);
      document.body.removeAttribute('data-page');
    };
  }, [location.pathname]);

  /* Match Layout participantPortalForceLight: global `dark:` still wins inside .force-light-mode unless we omit dark variants here. */
  const isParticipantPortalLoginShell =
    location.pathname === '/login/participant' ||
    location.pathname.startsWith('/home/') ||
    (location.pathname === '/' && isParticipantDomain());

  const headerSurface = isParticipantPortalLoginShell
    ? (isScrolled
        ? 'bg-white/90 backdrop-blur-md shadow-md border-b border-gray-200/50'
        : 'bg-primary-600 border-b border-transparent')
    : (isScrolled
        ? 'bg-white/90 dark:bg-gray-900/95 backdrop-blur-md shadow-md dark:shadow-gray-900/30 border-b border-gray-200/50 dark:border-gray-700/50'
        : 'bg-primary-600 dark:bg-gray-900 border-b border-transparent dark:border-gray-800');

  const signInLinkClasses = isParticipantPortalLoginShell
    ? (isScrolled
        ? 'bg-primary-600 text-white hover:bg-primary-700'
        : 'bg-white text-primary-600 hover:bg-white/90')
    : (isScrolled
        ? 'bg-primary-600 text-white hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-400'
        : 'bg-white text-primary-600 hover:bg-white/90 dark:bg-gray-800 dark:text-primary-400 dark:hover:bg-gray-700');

  const menuBtnClasses = isParticipantPortalLoginShell
    ? (isScrolled ? 'text-gray-700 hover:bg-gray-100' : 'text-white hover:bg-white/10')
    : (isScrolled
        ? 'text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-800'
        : 'text-white hover:bg-white/10');

  const dropdownPanelClasses = isParticipantPortalLoginShell
    ? 'rounded-xl overflow-hidden shadow-lg ring-1 ring-black/5 bg-white'
    : 'rounded-xl overflow-hidden shadow-lg ring-1 ring-black/5 dark:ring-gray-700 bg-white dark:bg-gray-800';

  const dropdownProfileBorder = isParticipantPortalLoginShell
    ? 'p-4 border-b border-gray-100'
    : 'p-4 border-b border-gray-100 dark:border-gray-700';

  const avatarWrapClasses = isParticipantPortalLoginShell
    ? 'bg-primary-50 p-2.5 rounded-full'
    : 'bg-primary-50 dark:bg-primary-900/30 p-2.5 rounded-full';

  const avatarIconClasses = isParticipantPortalLoginShell
    ? 'h-6 w-6 text-primary-600'
    : 'h-6 w-6 text-primary-600 dark:text-primary-400';

  const profileNameClasses = isParticipantPortalLoginShell
    ? 'text-sm font-medium text-gray-800 truncate'
    : 'text-sm font-medium text-gray-800 dark:text-gray-100 truncate';

  const profileMetaClasses = isParticipantPortalLoginShell
    ? 'text-xs text-gray-500 truncate'
    : 'text-xs text-gray-500 dark:text-gray-400 truncate';

  const menuItemActive = isParticipantPortalLoginShell
    ? 'bg-gray-50 text-primary-600 font-medium'
    : 'bg-gray-50 dark:bg-gray-700 text-primary-600 dark:text-primary-400 font-medium';

  const menuItemInactive = isParticipantPortalLoginShell
    ? 'text-gray-700 hover:bg-gray-50 hover:text-primary-600'
    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 hover:text-primary-600 dark:hover:text-primary-400';

  const menuItemIconActive = isParticipantPortalLoginShell
    ? 'text-primary-600'
    : 'text-primary-600 dark:text-primary-400';

  const menuItemIconInactive = isParticipantPortalLoginShell
    ? 'text-gray-400 group-hover:text-primary-600'
    : 'text-gray-400 dark:text-gray-500 group-hover:text-primary-600 dark:group-hover:text-primary-400';

  const chevronInactive = isParticipantPortalLoginShell
    ? 'ml-auto text-gray-300 opacity-0 group-hover:opacity-100 group-hover:text-gray-400'
    : 'ml-auto text-gray-300 dark:text-gray-600 opacity-0 group-hover:opacity-100 group-hover:text-gray-400 dark:group-hover:text-gray-500';

  const chevronActive = isParticipantPortalLoginShell
    ? 'ml-auto text-primary-600'
    : 'ml-auto text-primary-600 dark:text-primary-400';

  const logoutBadgeClasses = isParticipantPortalLoginShell
    ? 'ml-auto bg-red-50 text-red-500 text-xs px-1.5 py-0.5 rounded'
    : 'ml-auto bg-red-50 dark:bg-red-900/30 text-red-500 dark:text-red-400 text-xs px-1.5 py-0.5 rounded';

  const showGreenLogoWhenScrolled = isScrolled && (isParticipantPortalLoginShell || !isDarkMode);

  return (
    <header className={`fixed ${isLocalDevMode ? 'top-8' : 'top-0'} left-0 right-0 z-50 transition-all duration-300 ${headerSurface}`}>
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo and Brand */}
          <div className="flex-shrink-0 flex items-center">
            <motion.div 
              className="flex items-center cursor-pointer" 
              onClick={handleLogoClick}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <img 
                className="h-8 w-auto sm:h-10 md:h-12"
                src={showGreenLogoWhenScrolled ? vpgLogoHome : vpgLogoCondensed}
                alt="FlexCOA Logo" 
              />
            </motion.div>
          </div>

          {/* Right Side Items - Sign In button hidden on participant login with hash, menu shown for authenticated users */}
          <div className="flex items-center space-x-2">
            {/* Login Button - hidden only on participant login page with hash */}
            {!isAuthenticated && !paths.includes('login') && !isParticipantLoginWithHash ? (
              <motion.div
                className="sign-in-button-container"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Link 
                  to="/login" 
                  className={`sign-in-button px-4 py-2 rounded-full text-sm font-medium ${signInLinkClasses} transition-all duration-200 shadow-sm`}
                >
                  Sign In
                </Link>
              </motion.div>
            ) : isAuthenticated && shouldShowMenu ? (
              <div className="flex items-center space-x-1">
                {/* Notification Bell - shown for authenticated non-participant users */}
                {!isParticipantFlow && (
                  <NotificationBell isScrolled={isScrolled} />
                )}
                
                {/* Menu Button */}
                <div className="relative" ref={menuRef}>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => setIsMenuOpen(!isMenuOpen)}
                  className={`flex items-center justify-center p-2 rounded-full ${menuBtnClasses} transition-all duration-200`}
                  aria-label="Menu"
                >
                  {isMenuOpen ? (
                    <X size={20} strokeWidth={1.5} />
                  ) : (
                    <MenuIcon size={20} strokeWidth={1.5} />
                  )}
                </motion.button>

                <AnimatePresence mode="wait">
                  {isMenuOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 10, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 10, scale: 0.95 }}
                      transition={{ duration: 0.2, ease: "easeOut" }}
                      className="absolute right-0 mt-2 w-60 origin-top-right z-50"
                    >
                      <div className={dropdownPanelClasses}>
                        {/* User Profile Section */}
                        {user && (
                          <div className={dropdownProfileBorder}>
                            <div className="flex items-center">
                              <div className={avatarWrapClasses}>
                                <UserCircle className={avatarIconClasses} />
                              </div>
                              <div className="ml-3 overflow-hidden">
                                <p className={profileNameClasses}>
                                  {userType === 'participant' ? 'Participant' : (user?.username || user?.email || 'User')}
                                </p>
                                {userType !== 'participant' && (
                                  <p className={profileMetaClasses}>
                                    {user?.is_internal ? 'Internal User' : 'User'}
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Menu Items */}
                        <div className="py-1">
                          {menuItems.map((item, index) => (
                            <motion.button
                              key={item.label}
                              initial={{ opacity: 0, x: 20 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: 20 }}
                              transition={{ 
                                delay: index * 0.05,
                                duration: 0.15
                              }}
                              onClick={() => {
                                item.onClick();
                                setIsMenuOpen(false);
                              }}
                              className={`w-full flex items-center px-4 py-2.5 text-sm group ${
                                isActive(item.path) ? menuItemActive : menuItemInactive
                              } transition-colors duration-200`}
                            >
                              <item.icon 
                                size={18} 
                                className={`${
                                  isActive(item.path) ? menuItemIconActive : menuItemIconInactive
                                } transition-colors duration-200`} 
                                strokeWidth={1.5}
                              />
                              <span className="ml-3">
                                {item.label}
                              </span>
                              {item.label === 'Logout' ? (
                                <span className={logoutBadgeClasses}>
                                  Exit
                                </span>
                              ) : (
                                <ChevronRight 
                                  size={14} 
                                  className={`${
                                    isActive(item.path) ? chevronActive : chevronInactive
                                  } transition-all duration-200`}
                                  strokeWidth={1.5}
                                />
                              )}
                            </motion.button>
                          ))}
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
};

export default HomeNavMenu;
